/**
 * Depot manager slice (17/06/2026) — refonte dépôts.
 *
 * Owns:
 *   • `hiredDepotManagers` — { [managerId]: { hiredAt, assignedDepotId|null } }
 *   • Recrutement / licenciement / réaffectation des responsables de site.
 *
 * Un dépôt non-QG doit avoir un responsable assigné pour être opérationnel
 * (cf. `isDepotOperational` dans data/depotManagers.js).
 *
 * Coût mensuel agrégé : `getTotalManagerCost(state.ownedDepots)`, débité
 * dans `coreSlice.nextDay` au passage de mois.
 */
import { DEPOT_MANAGERS, getDepotManagerById } from '../../data/depotManagers';

export function createDepotManagerSlice(set, get) {
  return {
    hiredDepotManagers: {},

    /**
     * Recrute un responsable. Frais de signature : 1 mois de salaire payé
     * immédiatement (« prime d'embauche »). Retourne { ok, reason? }.
     */
    hireDepotManager: (managerId, targetDepotId = null) => {
      const state = get();
      const mgr = getDepotManagerById(managerId);
      if (!mgr) return { ok: false, reason: 'unknown_manager' };
      const hired = state.hiredDepotManagers || {};
      if (hired[managerId]) return { ok: false, reason: 'already_hired' };
      const signingBonus = mgr.monthlyCost;
      if ((state.treasury || 0) < signingBonus) {
        return { ok: false, reason: 'insufficient_funds', cost: signingBonus };
      }
      // Auto-assign to depot if provided + depot exists + no manager yet.
      let updatedDepots = state.ownedDepots || [];
      if (targetDepotId) {
        const dep = updatedDepots.find(d => d.id === targetDepotId);
        if (!dep) return { ok: false, reason: 'unknown_depot' };
        if (dep.isHeadquarters) return { ok: false, reason: 'hq_no_manager' };
        if (dep.managerId) return { ok: false, reason: 'depot_already_managed' };
        updatedDepots = updatedDepots.map(d => d.id === targetDepotId ? { ...d, managerId } : d);
      }
      set({
        treasury: (state.treasury || 0) - signingBonus,
        hiredDepotManagers: {
          ...hired,
          [managerId]: { hiredAt: state.totalDays || 0, assignedDepotId: targetDepotId || null },
        },
        ownedDepots: updatedDepots,
        notifications: [...(state.notifications || []).slice(-30), {
          id: `mgr-hire-${managerId}-${state.totalDays || 0}`,
          type: 'depot_manager_hired',
          title: 'Responsable de site recruté',
          message: `${mgr.name} rejoint l'entreprise (prime de signature ${signingBonus.toLocaleString('fr-FR')} €).`,
          day: state.totalDays || 0,
          read: false,
        }],
      });
      return { ok: true, signingBonus };
    },

    /**
     * Assigne (ou ré-assigne) un responsable déjà recruté à un dépôt.
     * Pas de coût, mais ne peut pas affecter au QG.
     */
    assignDepotManager: (managerId, depotId) => {
      const state = get();
      const hired = state.hiredDepotManagers || {};
      if (!hired[managerId]) return { ok: false, reason: 'not_hired' };
      const depots = state.ownedDepots || [];
      const dep = depots.find(d => d.id === depotId);
      if (!dep) return { ok: false, reason: 'unknown_depot' };
      if (dep.isHeadquarters) return { ok: false, reason: 'hq_no_manager' };
      // Détacher le manager de son ancien dépôt (si différent).
      const prevDepotId = hired[managerId].assignedDepotId;
      const updatedDepots = depots.map(d => {
        if (d.id === prevDepotId && d.id !== depotId) return { ...d, managerId: null };
        if (d.id === depotId) {
          // Si la cible avait déjà un autre manager → on le détache d'office.
          return { ...d, managerId };
        }
        return d;
      });
      // Si un autre manager occupait le dépôt cible, on libère son flag.
      const updatedHired = { ...hired };
      Object.keys(updatedHired).forEach(k => {
        if (k === managerId) {
          updatedHired[k] = { ...updatedHired[k], assignedDepotId: depotId };
        } else if (updatedHired[k].assignedDepotId === depotId) {
          updatedHired[k] = { ...updatedHired[k], assignedDepotId: null };
        }
      });
      set({ ownedDepots: updatedDepots, hiredDepotManagers: updatedHired });
      return { ok: true };
    },

    /**
     * Détache un responsable du dépôt qu'il occupe (sans le licencier).
     * Le dépôt devient non opérationnel.
     */
    unassignDepotManager: (managerId) => {
      const state = get();
      const hired = state.hiredDepotManagers || {};
      if (!hired[managerId]) return { ok: false, reason: 'not_hired' };
      const depotId = hired[managerId].assignedDepotId;
      set({
        ownedDepots: (state.ownedDepots || []).map(d =>
          d.id === depotId ? { ...d, managerId: null } : d,
        ),
        hiredDepotManagers: {
          ...hired,
          [managerId]: { ...hired[managerId], assignedDepotId: null },
        },
      });
      return { ok: true };
    },

    /**
     * Licencie un responsable. Indemnité simple = 1 mois.
     */
    fireDepotManager: (managerId) => {
      const state = get();
      const hired = state.hiredDepotManagers || {};
      if (!hired[managerId]) return { ok: false, reason: 'not_hired' };
      const mgr = getDepotManagerById(managerId);
      if (!mgr) return { ok: false, reason: 'unknown_manager' };
      const severance = mgr.monthlyCost;
      const depotId = hired[managerId].assignedDepotId;
      const newHired = { ...hired };
      delete newHired[managerId];
      set({
        treasury: (state.treasury || 0) - severance,
        ownedDepots: (state.ownedDepots || []).map(d =>
          d.id === depotId ? { ...d, managerId: null } : d,
        ),
        hiredDepotManagers: newHired,
        notifications: [...(state.notifications || []).slice(-30), {
          id: `mgr-fire-${managerId}-${state.totalDays || 0}`,
          type: 'depot_manager_fired',
          title: 'Responsable licencié',
          message: `${mgr.name} a quitté l'entreprise (indemnité ${severance.toLocaleString('fr-FR')} €).`,
          day: state.totalDays || 0,
          read: false,
        }],
      });
      return { ok: true, severance };
    },

    /**
     * Sélecteur — liste les responsables disponibles à l'embauche.
     */
    getAvailableDepotManagers: () => {
      const hired = get().hiredDepotManagers || {};
      return DEPOT_MANAGERS.filter(m => !hired[m.id]);
    },

    /**
     * Sélecteur — liste les responsables recrutés.
     */
    getHiredDepotManagers: () => {
      const hired = get().hiredDepotManagers || {};
      return DEPOT_MANAGERS.filter(m => !!hired[m.id]).map(m => ({
        ...m,
        ...hired[m.id],
      }));
    },
  };
}
